import { isDefined, isString } from '@sgme/fp';

import type { WebapiEndpointBuilder } from '@/api/webapi.ts';
import { logger } from '@/components/SgwtBootstrap/logger.ts';
import type { FixingMarginType, FixingPriceType } from '@/store/bulkOrder/state.ts';
import type { SubmitFixingOrderRequest } from '@/store/orders/fixing/state.ts';
import type { SubmitOrderResponse, ValidationResponseDetailContent } from '@/store/orders/state.ts';
import { toBase64 } from '@/utils/toBase64.ts';

export type SubmitFixingOrderBody = {
  isGtc: boolean;
  bdrId: string;
  amountInCcy1: number | undefined;
  amountInCcy2: number | undefined;
  ccyPair: string;
  way: 'buy' | 'sell';
  base64Emails: string;
  localTimeWithOffSet: number;
  fixingBenchmark: string | undefined;
  fixingPriceType: FixingPriceType | undefined;
  fixingDateUtc: string | undefined;
  expiryDay: string | undefined;
  fixingTime: string | undefined;
  fixingPlace: string | undefined;
  marginInBps: number | undefined;
  fixingMarginType: FixingMarginType | undefined;
};

export const submitFixing = (builder: WebapiEndpointBuilder) => {
  return builder.mutation<SubmitOrderResponse, SubmitFixingOrderRequest>({
    query: (request) => ({
      url: '/oms/submit/Fixing',
      method: 'POST',
      body: generateBody(request),
      headers: {
        // without this header, the response is a string
        // instead of JSON, even if the value is "application/json"
        accept: '*/*',
      },
    }),
    transformResponse: (
      response:
        | string
        | {
            validationResponseDetailContent: ValidationResponseDetailContent[];
          },
    ) => {
      return {
        type: 'success',
        orderId: response as string, // assuming the response is a string with the order ID
      };
    },
    transformErrorResponse: (errorResponse, _meta, arg): SubmitOrderResponse => {
      const tileId = arg.tileId;
      if (errorResponse.status === 'TIMEOUT_ERROR' || errorResponse.status === 504) {
        return {
          type: 'failure',
          errors: [
            {
              field: 'unknown',
              errorDescription: 'timeout',
              errorCode: -1,
            },
          ],
        };
      }

      const data = errorResponse.data as
        | string
        | {
            validationResponseDetailContent: ValidationResponseDetailContent[];
          };
      if (typeof errorResponse.status === 'number' && errorResponse.status >= 400 && errorResponse.status <= 499) {
        logger.logError(
          `Order submission failed because of a validation field rejection. orderId: {tileId_s},
            response: {data_s}`,
          tileId,
          JSON.stringify(data),
        );
        if (isValidateOrderResponse(data)) {
          return {
            type: 'failure',
            errors: data?.validationResponseDetailContent,
          };
        }

        if (isString(data)) {
          return {
            type: 'failure',
            errors: [
              {
                field: 'unknown',
                errorDescription: data,
                errorCode: -1,
              },
            ],
          };
        }
      }

      if (typeof errorResponse.status === 'number' && errorResponse.status >= 500 && errorResponse.status <= 599) {
        logger.logError(
          'Backend crashed with error 500 for orderId: {tileId_s}, response: {data_s}',
          tileId,
          JSON.stringify(data),
        );
        return {
          type: 'failure',
          errors: [
            {
              field: 'unknown',
              errorDescription: 'server.error',
              errorCode: -1,
            },
          ],
        };
      }

      logger.logError(
        'Backend returned an unexpected error {status_s} for orderId: {tileId_s}, response: {data_s}',
        errorResponse.status,
        tileId,
        JSON.stringify(data),
      );

      return {
        type: 'failure',
        errors: [
          {
            field: 'unknown',
            errorDescription: 'order.unexpected',
            errorCode: -1,
          },
        ],
      };
    },
  });
};

const generateBody = (order: SubmitFixingOrderRequest): SubmitFixingOrderBody => ({
  isGtc: true,
  bdrId: String(order.clientId),
  ccyPair: `${order?.currency1}/${order?.currency2}`,
  way: order.way ?? 'buy',
  amountInCcy1: order.notionalCurrencyIndex === 1 ? order.notional : undefined,
  amountInCcy2: order.notionalCurrencyIndex === 2 ? order.notional : undefined,
  fixingBenchmark: order.fixingReference,
  fixingPriceType: order.fixingPriceType,
  fixingDateUtc: order.fixingDateUtc ? order.fixingDateUtc : undefined,
  expiryDay: order.expiryDay ? order.expiryDay : undefined,
  fixingMarginType: order.fixingMarginType ? order.fixingMarginType : undefined,
  fixingTime: order.fixingTime,
  marginInBps: order.commercialMargin ? order.commercialMargin : undefined,
  fixingPlace: order.fixingPlace,
  base64Emails: isDefined(order?.notificationEmails) ? toBase64(order?.notificationEmails.join(';')) : '',
  localTimeWithOffSet: order.clockOffset !== undefined ? Date.now() + order.clockOffset : 0,
});

function isValidateOrderResponse(response: unknown): response is {
  validationResponseDetailContent: ValidationResponseDetailContent[];
  isReadyToSubmit: boolean;
} {
  return (
    typeof response === 'object' &&
    response !== null &&
    'validationResponseDetailContent' in response &&
    'isReadyToSubmit' in response &&
    Array.isArray(response.validationResponseDetailContent)
  );
}
